/**
 * BackgroundJobRow — one entry in the background jobs tray.
 * Running rows are rendered inert (not clickable). Completed/failed rows with
 * a known deeplink_kind dispatch TASK_CLICKED on click.
 */

import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { BackgroundTask } from '@/infra/types/domain';
import { taskClicked } from '../ducks';

const HUMAN_TASK_TYPE: Record<string, string> = {
  enhance_contact: 'Enhance contact',
};

const STATUS_LABEL: Record<string, string> = {
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
  no_suggestions: 'No suggestions',
};

function humanizeTaskType(t: string): string {
  if (HUMAN_TASK_TYPE[t]) return HUMAN_TASK_TYPE[t];
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

interface Props {
  task: BackgroundTask;
}

export function BackgroundJobRow({ task }: Props) {
  const dispatch = useDispatch();
  const clickable = task.status !== 'running' && Boolean(task.deeplink_kind);

  const handleClick = useCallback(() => {
    if (!clickable) return;
    dispatch(taskClicked(task));
  }, [dispatch, task, clickable]);

  return (
    <div
      className={`bg-job-row bg-job-row--${task.status}${clickable ? ' bg-job-row--clickable' : ''}`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      data-testid={`bg-job-row-${task.id}`}
    >
      <div className="bg-job-row__main">
        <span className="bg-job-row__type">{humanizeTaskType(task.task_type)}</span>
        {task.subject_label && (
          <span className="bg-job-row__subject">{task.subject_label}</span>
        )}
      </div>
      <div className="bg-job-row__meta">
        <span className={`bg-job-row__status bg-job-row__status--${task.status}`}>
          {STATUS_LABEL[task.status] ?? task.status}
        </span>
        <span className="bg-job-row__time">{relativeTime(task.started_at)}</span>
      </div>
    </div>
  );
}

export default BackgroundJobRow;
